import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { ServicePathsSettings } from '../../../../core/configuration/service-paths-settings';
import { FileManagerService } from '../../../../core/components/upload-tool/services/file-manager.service';
import { HelpersService } from '../../../../core/services/helpers.service';
import { OperationsService } from '../../../../core/services/operations.service';
import { AttiFormComponent } from '../../../form/atti-form.component';
import { DocumentoAllegato } from '../../../models/documento-allegato';
import { DataInput } from '../../../models/dto/data-input';
import { UpdateMetadatiDocumentoRequest } from '../../../models/dto/update-metadati-documento-request';
import { Atto } from '../../../models/atto';
import { SezioneAttiFull } from '../../../models/sezioni/sezione-atti-full';
import { FascicoloFull } from 'src/app/common/models/fascicolo/fascicolo-full';
import { SpinnerService } from '../../spinner-load/spinner.service';

@Component({
    selector: 'div [atti-form-add]',
  templateUrl: '../../../form/atti-form.component.html'
})

export class AttiAddConcreteFormComponent extends AttiFormComponent {
  idSezione: string;
  idFascicolo: string;
  urlInsert: string;
  fascicolo!: FascicoloFull;
  @Output() onReturn: EventEmitter<SezioneAttiFull> = new EventEmitter<SezioneAttiFull>();
  @Output() onAttoInseritoForAuto: EventEmitter<any> = new EventEmitter<any>();
  @Output() onDocumentiCaricati: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    @Inject(OperationsService) operationService: OperationsService,
    @Inject(FormBuilder) campiForm: FormBuilder,
    @Inject(HelpersService) helpers: HelpersService,
    private http: HttpClient,
    private fileManager: FileManagerService,
    private spinner: SpinnerService,
  ) {
        super(operationService, campiForm, helpers);
  }

  ngOnInit() {
    this.setTitle('Aggiungi Atto');
    this.isInserimento(true);
  }

  getAtto() {
    return new Atto();
  }

  getFascicolo(): FascicoloFull {
    return this.fascicolo;
  }

  getRequest(atto: Atto) {
    this.operationService.addAtto(this.urlInsert, atto, this.idFascicolo, this.idSezione).subscribe(response => this.datiAtto(response, atto));
  }

  datiAtto(response: any, atto: Atto) {
    if (response.returnCode === 'OK') {
      if (this.fileUploadList.length > 0) {
        this.uploadDocsToAtto(response.lastID, response, atto);
      } else {
        this.onReturn.emit(response);
        this.onAttoInseritoForAuto.emit(atto);
      }
    }
  }

  private uploadDocsToAtto(attoId: string, attoResponse: any, atto: Atto) {
    const files = this.fileUploadList.splice(0, this.fileUploadList.length).map(f => this.enrichDocFile(f));
    forkJoin(
      files.map(f =>
        this.operationService.addDocumentoToEntity(ServicePathsSettings.URL_ADD_DOCUMENTO_TO_ATTO, attoId, f)
          .pipe(map((res: any) => ({ res, file: f })))
      )
    ).pipe(
      switchMap(results => {
        const metadatiObs = results
          .filter(({ res }) => res.returnCode === 'OK')
          .map(({ res, file }) => {
            const docs: DocumentoAllegato[] = res?.atto?.documenti ?? [];
            const saved = docs.find((d: DocumentoAllegato) => d.refDocumentale === file.refDocumentale);
            return saved ? this.updateMetadatiDocumento(saved) : of(null);
          });
        return metadatiObs.length ? forkJoin(metadatiObs) : of([]);
      })
    ).subscribe({
      next: () => {
        this.onReturn.emit(attoResponse);
        this.onAttoInseritoForAuto.emit(atto);
        this.onDocumentiCaricati.emit();
      },
      error: () => {
        this.onReturn.emit(attoResponse);
        this.onAttoInseritoForAuto.emit(atto);
      }
    });
  }

  private updateMetadatiDocumento(documento: DocumentoAllegato): Observable<any> {
    const request = new UpdateMetadatiDocumentoRequest();
    request.documento = { ...documento };
    if (this.fascicolo?.praticaBase) {
      request.dataInput = new DataInput(
        this.fascicolo.idParent,
        this.fascicolo.id,
        this.fascicolo.praticaBase.posizione,
        this.fascicolo.posizione,
        this.fascicolo.praticaBase.tipoPratica.id
      );
    }
    return this.operationService.updateMetadatiDocumento(request);
  }

  private enrichDocFile(file: DocumentoAllegato): DocumentoAllegato {
    const { signedData, ...fileWithoutSignedData } = file;
    const titolo = file.nomeFile
      ? file.nomeFile.replace(/\.[^/.]+$/, '')
      : (this.docFormRequest.titolo?.trim() || '');
    return {
      ...fileWithoutSignedData,
      titolo,
      tipoDocumento: this.docFormRequest.tipoDocumento,
      dataRiferimento: this.docFormRequest.dataRiferimento,
      provenienza: this.docFormRequest.provenienza || null,
      tipoVisibilitaDocumento: this.docFormRequest.tipoVisibilitaDocumento,
      ...(this.docFormRequest.note ? { note: '' + this.docFormRequest.note } : {})
    };
  }

  override onSearchChange(files: any) {
    if (!files?.length) return;
    this.docLoading = true;
    this.spinner.show();
    const uploads$ = Array.from(files).map((file: any) =>
      this.fileManager.getPayloadFile(file).pipe(
        switchMap(payload => this.uploadFile(payload).pipe(
          catchError(err => { console.error('Errore upload:', err); return of(null); })
        ))
      )
    );
    forkJoin(uploads$).subscribe({
      next: (responses: any[]) => {
        responses.forEach(res => {
          if (res?.fileData) this.setFileDataRequest(res.fileData);
        });
      },
      complete: () => {
        this.docLoading = false;
        this.spinner.hide();
      }
    });
  }

  private uploadFile(payload: any): Observable<any> {
    return this.http.post<any>(
      ServicePathsSettings.DOMINIO + ServicePathsSettings.URL_UPLOAD_DOCUMENTO,
      payload,
      { withCredentials: true }
    );
  }

  inizializza(idSezione: string, idFascicolo: string, urlInsert: string, fascicolo: FascicoloFull) {
    this.idSezione = idSezione;
    this.idFascicolo = idFascicolo;
    this.urlInsert = urlInsert;
    this.fascicolo = fascicolo;
    this.createForm(false);
    this.initDocForm();
  }

}
